class CustomCollectionCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('[data-carousel-track]');
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.progressBar = this.querySelector('[data-carousel-progress]');
    
    this.slides = Array.from(this.querySelectorAll('.cc-carousel__slide'));
    this.totalSlides = this.slides.length;
    this.currentIndex = 0;
    
    this.desktopPerView = parseInt(this.dataset.desktopPerView) || 4;
    this.mobilePerView = parseInt(this.dataset.mobilePerView) || 2;
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = (parseInt(this.dataset.autoplaySpeed) || 5) * 1000;
    this.loop = this.dataset.loop === 'true';
    this.gap = parseInt(this.dataset.gap) || 20;
    
    this.autoplayInterval = null;
    this.isMobile = window.innerWidth <= 768;
    this.slidesPerView = this.isMobile ? this.mobilePerView : this.desktopPerView;
    
    this.dragStartX = 0;
    this.dragStartScroll = 0;
    this.isDragging = false;
    this.dragThreshold = 50;
    this.isTransitioning = false;
    
    // Clone slides for seamless infinite loop
    this.clonedSlidesAdded = false;
    
    this.init();
  }

  init() {
    if (this.totalSlides === 0) return;
    
    this.setSlideWidths();
    
    if (this.loop && this.totalSlides > this.slidesPerView) {
      this.addClonedSlides();
    }
    
    // Set initial position
    setTimeout(() => {
      this.updateCarousel(true);
    }, 10);
    
    this.addEventListeners();
    
    if (this.autoplay && this.totalSlides > this.slidesPerView) {
      this.startAutoplay();
    }
  }

  addClonedSlides() {
    if (this.clonedSlidesAdded) return;
    
    // Clone first few slides and append to end
    const slidesToClone = Math.min(this.slidesPerView, this.totalSlides);
    for (let i = 0; i < slidesToClone; i++) {
      const clone = this.slides[i].cloneNode(true);
      clone.classList.add('cc-carousel__slide--cloned');
      this.track.appendChild(clone);
    }
    
    // Clone last few slides and prepend to beginning
    for (let i = this.totalSlides - slidesToClone; i < this.totalSlides; i++) {
      const clone = this.slides[i].cloneNode(true);
      clone.classList.add('cc-carousel__slide--cloned');
      this.track.insertBefore(clone, this.track.firstChild);
    }
    
    // Update slides collection
    this.slides = Array.from(this.querySelectorAll('.cc-carousel__slide'));
    
    // Set initial index to account for prepended clones
    this.currentIndex = slidesToClone;
    
    this.clonedSlidesAdded = true;
  }

  setSlideWidths() {
    this.style.setProperty('--gap', `${this.gap}px`);
  }

  getMaxIndex() {
    if (this.loop) {
      // In loop mode with clones, we can scroll through all original slides
      return this.slides.length - this.slidesPerView;
    }
    return Math.max(0, this.totalSlides - this.slidesPerView);
  }

  getProgressMaxIndex() {
    // For progress bar, we only count original slides
    return Math.max(0, this.totalSlides - this.slidesPerView);
  }

  updateCarousel(instant = false) {
    if (!this.track || this.isTransitioning) return;
    
    if (instant) {
      this.track.style.transition = 'none';
    } else {
      this.track.style.transition = 'transform 0.5s ease';
    }
    
    const slide = this.slides[0];
    if (!slide) return;
    
    const slideWidth = slide.offsetWidth;
    const translateX = -this.currentIndex * (slideWidth + this.gap);
    
    this.track.style.transform = `translateX(${translateX}px)`;
    
    this.updateProgress();
    this.updateButtons();
    
    // Handle infinite loop reset without transition
    if (this.loop && !instant) {
      this.handleLoopReset();
    }
  }

  handleLoopReset() {
    const slidesToClone = Math.min(this.slidesPerView, this.totalSlides);
    
    // If we've scrolled to the cloned slides at the end
    if (this.currentIndex >= this.totalSlides + slidesToClone) {
      this.isTransitioning = true;
      
      setTimeout(() => {
        this.track.style.transition = 'none';
        this.currentIndex = slidesToClone;
        const slideWidth = this.slides[0].offsetWidth;
        const translateX = -this.currentIndex * (slideWidth + this.gap);
        this.track.style.transform = `translateX(${translateX}px)`;
        
        setTimeout(() => {
          this.track.style.transition = 'transform 0.5s ease';
          this.isTransitioning = false;
        }, 50);
      }, 500);
    }
    
    // If we've scrolled to the cloned slides at the beginning
    if (this.currentIndex < slidesToClone) {
      this.isTransitioning = true;
      
      setTimeout(() => {
        this.track.style.transition = 'none';
        this.currentIndex = this.totalSlides + slidesToClone - (slidesToClone - this.currentIndex);
        const slideWidth = this.slides[0].offsetWidth;
        const translateX = -this.currentIndex * (slideWidth + this.gap);
        this.track.style.transform = `translateX(${translateX}px)`;
        
        setTimeout(() => {
          this.track.style.transition = 'transform 0.5s ease';
          this.isTransitioning = false;
        }, 50);
      }, 500);
    }
  }

  updateProgress() {
    if (!this.progressBar) return;
    
    const maxIndex = this.getProgressMaxIndex();
    if (maxIndex === 0) {
      this.progressBar.style.width = '0%';
      return;
    }
    
    // Calculate progress based on original slides only
    let progressIndex = this.currentIndex;
    
    if (this.loop) {
      const slidesToClone = Math.min(this.slidesPerView, this.totalSlides);
      // Adjust index to account for cloned slides
      if (this.currentIndex >= slidesToClone && 
          this.currentIndex < slidesToClone + this.totalSlides) {
        progressIndex = this.currentIndex - slidesToClone;
      } else if (this.currentIndex >= slidesToClone + this.totalSlides) {
        progressIndex = this.totalSlides - 1;
      } else {
        progressIndex = 0;
      }
    }
    
    // Ensure progressIndex is within bounds
    progressIndex = Math.max(0, Math.min(progressIndex, maxIndex));
    
    const progress = (progressIndex / maxIndex) * 100;
    this.progressBar.style.width = `${progress}%`;
  }

  updateButtons() {
    if (!this.prevButton || !this.nextButton) return;
    
    if (!this.loop) {
      const maxIndex = this.getMaxIndex();
      this.prevButton.disabled = this.currentIndex === 0;
      this.nextButton.disabled = this.currentIndex >= maxIndex;
    } else {
      this.prevButton.disabled = false;
      this.nextButton.disabled = false;
    }
  }

  nextSlide() {
    if (this.isTransitioning) return;
    
    this.currentIndex++;
    this.updateCarousel();
    this.restartAutoplay();
  }

  prevSlide() {
    if (this.isTransitioning) return;
    
    this.currentIndex--;
    this.updateCarousel();
    this.restartAutoplay();
  }

  goToSlide(index) {
    if (this.isTransitioning) return;
    
    if (this.loop) {
      const slidesToClone = Math.min(this.slidesPerView, this.totalSlides);
      this.currentIndex = slidesToClone + index;
    } else {
      this.currentIndex = index;
    }
    
    this.updateCarousel();
    this.restartAutoplay();
  }

  // Draggable functionality with momentum
  startDrag(e) {
    if (this.isTransitioning) return;
    
    this.isDragging = true;
    this.dragStartX = e.pageX || e.touches[0].pageX;
    this.dragStartScroll = this.currentIndex;
    this.track.style.cursor = 'grabbing';
    this.track.style.transition = 'none';
    this.pauseAutoplay();
  }

  onDrag(e) {
    if (!this.isDragging || this.isTransitioning) return;
    e.preventDefault();
    
    const currentX = e.pageX || e.touches[0].pageX;
    const diff = currentX - this.dragStartX;
    const slideWidth = this.slides[0]?.offsetWidth + this.gap || 0;
    
    // Live drag tracking
    const dragOffset = (diff / slideWidth);
    let newIndex = this.dragStartScroll - dragOffset;
    
    // Constrain index for non-loop mode
    if (!this.loop) {
      newIndex = Math.max(0, Math.min(newIndex, this.getMaxIndex()));
    }
    
    // Apply transform directly during drag
    const translateX = -newIndex * slideWidth;
    this.track.style.transform = `translateX(${translateX}px)`;
    
    // Update current index for progress bar during drag
    if (!this.loop) {
      this.currentIndex = Math.round(newIndex);
      this.updateProgress();
    }
  }

  endDrag() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.track.style.cursor = 'grab';
    this.track.style.transition = 'transform 0.5s ease';
    
    // Snap to nearest slide
    const slideWidth = this.slides[0]?.offsetWidth + this.gap || 0;
    const transform = this.track.style.transform;
    const match = transform.match(/translateX\((-?\d+)/);
    
    if (match) {
      const translateX = parseInt(match[1]);
      let newIndex = Math.round(-translateX / slideWidth);
      
      if (!this.loop) {
        newIndex = Math.max(0, Math.min(newIndex, this.getMaxIndex()));
      }
      
      this.currentIndex = newIndex;
      this.updateCarousel();
    }
    
    if (this.autoplay) this.startAutoplay();
  }

  // Autoplay
  startAutoplay() {
    this.pauseAutoplay();
    if (this.totalSlides <= this.slidesPerView) return;
    
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }

  pauseAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  restartAutoplay() {
    if (this.autoplay) {
      this.pauseAutoplay();
      this.startAutoplay();
    }
  }

  // Resize handler
  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 768;
    this.slidesPerView = this.isMobile ? this.mobilePerView : this.desktopPerView;
    
    if (wasMobile !== this.isMobile) {
      if (this.loop) {
        const slidesToClone = Math.min(this.slidesPerView, this.totalSlides);
        this.currentIndex = slidesToClone;
      } else {
        this.currentIndex = 0;
      }
    }
    
    this.updateCarousel(true);
  }

  addEventListeners() {
    // Arrow buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prevSlide());
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.nextSlide());
    }
    
    // Draggable mouse events
    this.track.addEventListener('mousedown', (e) => this.startDrag(e));
    this.track.addEventListener('mousemove', (e) => this.onDrag(e));
    this.track.addEventListener('mouseup', () => this.endDrag());
    this.track.addEventListener('mouseleave', () => this.endDrag());
    
    // Touch events
    this.track.addEventListener('touchstart', (e) => this.startDrag(e), { passive: true });
    this.track.addEventListener('touchmove', (e) => this.onDrag(e), { passive: false });
    this.track.addEventListener('touchend', () => this.endDrag());
    this.track.addEventListener('touchcancel', () => this.endDrag());
    
    // Pause autoplay on hover
    if (this.autoplay) {
      this.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
    }
    
    // Window resize
    window.addEventListener('resize', () => this.handleResize());
    
    // Prevent default drag on images
    this.querySelectorAll('img').forEach(img => {
      img.addEventListener('dragstart', (e) => e.preventDefault());
    });
  }

  disconnectedCallback() {
    this.pauseAutoplay();
    window.removeEventListener('resize', () => this.handleResize());
  }
}

customElements.define('carousel-component', CustomCollectionCarousel);